import { t } from "@hakili/i18n";
import type { KeyboardEvent, ReactElement } from "react";

/**
 * `S1-a` COMPORTEMENT DU COMPTE (`D2-behaviour.md` §3, §8, §12): **the two tabs under the stage track, « Dossier » and « Comportement du compte »**, on every dossier. Bank-web's own markup, no change to the
 * kit: `role="tablist"`, a ROVING tabindex (only the selected tab is a tab stop, the other is reached with the arrow keys), Left and Right move between the tabs and select, Home and End the first and the last
 * (the record's keyboard walk of §8: « Left and Right move between « Dossier » and « Comportement du compte » and select (aria-selected, roving tabindex) »).
 */
export type DossierTab = "dossier" | "comportement";

const ORDER: readonly DossierTab[] = ["dossier", "comportement"];

export function DossierTabs(props: {
  readonly active: DossierTab;
  readonly onSelect: (tab: DossierTab) => void;
}): ReactElement {
  const move = (event: KeyboardEvent<HTMLDivElement>): void => {
    const at = ORDER.indexOf(props.active);
    const next =
      event.key === "ArrowRight"
        ? ORDER[(at + 1) % ORDER.length]
        : event.key === "ArrowLeft"
          ? ORDER[(at + ORDER.length - 1) % ORDER.length]
          : event.key === "Home"
            ? ORDER[0]
            : event.key === "End"
              ? ORDER[ORDER.length - 1]
              : undefined;
    if (next === undefined) return;
    event.preventDefault();
    props.onSelect(next);
    // The roving tabindex follows the selection: the selected tab takes the focus the arrow key moved.
    globalThis.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(`[data-testid="bank-onglet-${next}"]`)?.focus();
    });
  };
  const tab = (id: DossierTab, label: string): ReactElement => {
    const selected = props.active === id;
    return (
      <button
        type="button"
        role="tab"
        id={`bank-onglet-${id}`}
        aria-selected={selected}
        tabIndex={selected ? 0 : -1}
        data-testid={`bank-onglet-${id}`}
        onClick={() => {
          if (!selected) props.onSelect(id);
        }}
        className={`-mb-px border-b-2 px-[18px] py-[10px] text-[length:var(--hk-fs-read)] font-medium ${
          selected
            ? "border-indigo-600 text-indigo-700"
            : "border-transparent text-[color:var(--hk-text-2)] hover:text-indigo-700"
        }`}
      >
        {label}
      </button>
    );
  };
  return (
    <div
      role="tablist"
      aria-label={t("bank.comportement.onglets")}
      data-testid="bank-onglets"
      onKeyDown={move}
      className="mb-5 flex border-b border-gray-200"
    >
      {tab("dossier", t("bank.comportement.dossierOnglet"))}
      {tab("comportement", t("bank.comportement.onglet"))}
    </div>
  );
}
